/** Pixa — stok sayfası. (Stok Hareketleri, Depo & WMS ve Satın Alma kaldırıldı:
 *  Pixa bu uçlar için bu kiracıda sıfır kayıt döndürüyor, uçlar backend'de duruyor.) */
import React, { useState, useEffect } from 'react';
import {
  API, PAGE_SIZE, T, MONO, FONT, Button, Card, Col, DataTable, Eyebrow, FilterBar, Tabs, Thumb,
  axios, fmtDate, fmtDateTime, fmtN, inputStyle, money, useLang, useList,
} from './shared';

// ── Stok (raf bazlı) ─────────────────────────────────────────────────────────
export function InventoryPage({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const [offset, setOffset] = useState(0);
  const [barcode, setBarcode] = useState(''); const [deb, setDeb] = useState('');
  const [editing, setEditing] = useState<any | null>(null);
  const [newQty, setNewQty] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  useEffect(() => { const id = setTimeout(() => { setDeb(barcode); setOffset(0); }, 400); return () => clearTimeout(id); }, [barcode]);

  const low = useList(clientId, '/inventory/low-stock', { limit: 10 });
  const { rows, pagination, loading, error, reload } = useList(clientId, '/inventory', {
    limit: PAGE_SIZE, offset, barcode: deb.trim() || undefined,
  });

  const save = async () => {
    if (!editing) return;
    const qty = parseInt(newQty, 10);
    if (isNaN(qty) || qty < 0) { setModalError(t('Geçerli bir miktar girin.')); return; }
    setSaving(true); setModalError('');
    try {
      await axios.patch(`${API}/pixa/${clientId}/inventory/stock`, { shelfStockId: editing.id, setQuantity: qty });
      setEditing(null); setNewQty(''); reload();
    } catch (e: any) { setModalError(e?.response?.data?.detail || t('Hata')); }
    finally { setSaving(false); }
  };

  const cols: Col[] = [
    { key: 'productName', label: 'Ürün', align: 'left', render: r => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Thumb url={r.thumbUrl} size={32} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: T.text, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.productName || '—'}</div>
          <div style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{[r.productSku, r.barcode].filter(Boolean).join(' · ')}</div>
        </div>
      </div>) },
    { key: 'shelfName', label: 'Raf', render: r => r.shelfName || `#${r.shelfId}` },
    { key: 'warehouseName', label: 'Depo', render: r => r.warehouseName || '—' },
    { key: 'quantity', label: 'Miktar', render: r => <span style={{ fontWeight: 600, color: T.text }}>{fmtN(r.quantity)}</span> },
    { key: 'orderRezervedQuantity', label: 'Rezerve', render: r => fmtN(r.orderRezervedQuantity) },
    { key: 'lockedQuantity', label: 'Kilitli', render: r => fmtN(r.lockedQuantity) },
    { key: 'available', label: 'Kullanılabilir', render: r => <span style={{ fontWeight: 700, color: Number(r.available) > 0 ? T.greenText : T.redText }}>{fmtN(r.available)}</span> },
    { key: 'actions', label: '', render: r => (
      <Button variant="ghost" color={T.accent} onClick={() => { setEditing(r); setNewQty(String(r.quantity ?? 0)); setModalError(''); }} style={{ padding: '4px 10px', fontSize: 11 }}>{t('Düzelt')}</Button>) },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      {low.rows.length > 0 && (
        <Card pad={18}>
          {(() => {
            // Pixa "kritik stok" listesinde eşik 0 iken aslında stoğu tükenmiş ürünler geliyor.
            const zero = low.rows.filter((r: any) => Number(r.totalStock) <= 0).length;
            return <>
              <Eyebrow color={T.redText}>{zero === low.rows.length ? t('Stoğu Tükenen Ürünler') : t('Kritik Stok')}</Eyebrow>
              <div style={{ fontSize: 12, color: T.textMid, margin: '6px 0 12px', lineHeight: 1.55 }}>
                {zero === low.rows.length
                  ? `${low.rows.length} ${t('ürünün stoğu tükendi; hiçbir kanalda satılamıyor. Tedarik edin ya da reklamlarda bu ürünleri durdurun.')}`
                  : `${low.rows.length} ${t('ürün kritik eşiğin altında.')}`}
              </div>
            </>;
          })()}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {low.rows.slice(0, 8).map((r: any) => (
              <div key={r.productId} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, padding: '6px 0', borderBottom: `1px solid ${T.card}` }}>
                <span style={{ color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.name}</span>
                <span style={{ fontFamily: MONO, fontSize: 11, color: T.textDim, flexShrink: 0 }}>{r.sku} · <b style={{ color: T.redText }}>{fmtN(r.totalStock)}</b></span>
              </div>
            ))}
          </div>
        </Card>
      )}
      <FilterBar>
        <input value={barcode} onChange={e => setBarcode(e.target.value)} placeholder={t('Barkod ile ara')} style={{ ...inputStyle, minWidth: 220 }} />
      </FilterBar>
      <DataTable title={t('Stok')} subtitle={t('Raf bazlı stok. Kullanılabilir = miktar − siparişe ayrılan − kilitli.')} cols={cols} rows={rows} loading={loading} error={error}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName="pixa-stok.csv" minWidth={900} />

      {editing && (
        <div onClick={() => !saving && setEditing(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: 14, border: `1px solid ${T.border}`, padding: '24px 26px', width: '100%', maxWidth: 400, fontFamily: FONT }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 6 }}>{t('Stok Düzelt')}</div>
            <div style={{ fontSize: 12, color: T.textMid, marginBottom: 16 }}>
              <span style={{ fontFamily: MONO }}>{editing.barcode}</span> · {editing.shelfName || `#${editing.shelfId}`} / {editing.warehouseName}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 14 }}>
              <span style={{ fontFamily: MONO, fontWeight: 700, color: T.textMid }}>{fmtN(editing.quantity)}</span>
              <span style={{ color: T.textDim }}>→</span>
              <input type="number" min={0} value={newQty} onChange={e => setNewQty(e.target.value)} autoFocus
                style={{ ...inputStyle, width: 110, fontFamily: MONO, fontWeight: 700, fontSize: 14 }} />
            </div>
            <div style={{ fontSize: 11, color: T.amberText, background: T.amberTint, padding: '8px 10px', borderRadius: 8, marginBottom: 16 }}>
              {t('Bu işlem Pixa tarafında kalıcı bir stok hareketi oluşturur.')}
            </div>
            {modalError && <div style={{ fontSize: 11.5, color: T.redText, marginBottom: 10 }}>{modalError}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" color={T.textDim} disabled={saving} onClick={() => setEditing(null)} style={{ flex: 1 }}>{t('Vazgeç')}</Button>
              <Button variant="solid" color={T.accent} disabled={saving} onClick={save} style={{ flex: 1 }}>{saving ? t('Kaydediliyor…') : t('Kaydet')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}




/** Sekmeli basit liste sayfası — WMS ve Kayıtlar aynı kalıbı kullanıyor. */
export function TabbedList({ clientId, tab, setTab, conf, tabs, params }: {
  clientId: number; tab: string; setTab: (v: string) => void; conf: any;
  tabs: { id: string; label: string }[]; params?: Record<string, any>;
}) {
  const { t } = useLang();
  const [offset, setOffset] = useState(0);
  useEffect(() => { setOffset(0); }, [tab]);
  const c = conf[tab];
  const { rows, pagination, loading, error } = useList(clientId, c.path, { limit: PAGE_SIZE, offset, ...(params || {}) });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {c.note && (
        <div style={{ fontSize: 11.5, color: T.textDim, lineHeight: 1.6, padding: '0 2px' }}>{t(c.note)}</div>
      )}
      <DataTable title={t(c.title)} cols={c.cols} rows={rows} loading={loading} error={error}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName={c.csv} minWidth={c.minWidth || 800} />
    </div>
  );
}
